import React, { useState, useEffect, useRef } from 'react';
import { Check, AlertCircle, Shield } from 'lucide-react';
import axios from 'axios';
import AppHeader, { PRIMARY_PAGES, SECONDARY_PAGES } from './components/layout/AppHeader';
import HomePage from './components/pages/HomePage';
import AboutPage from './components/pages/AboutPage';
import CliPage from './components/pages/CliPage';
import UseCasesPage from './components/pages/UseCasesPage';
import PricingPage from './components/pages/PricingPage';
import PrivacyPage from './components/pages/PrivacyPage';
import { ALERT_ERROR, ALERT_SUCCESS, FOOTER_LINK, TEXT } from './uiTokens';
import { API_TIMEOUT_MS, MAX_SECRET_LENGTH } from './constants';
import { getApiErrorMessage, isConsumedOrExpiredError } from './utils/apiErrors';
import { copyToClipboard } from './utils/clipboard';
import {
  generateAesKey,
  exportKeyToFragment,
  importKeyFromFragment,
  encryptSecret,
  decryptSecret,
  readKeyFromLocation,
  isValidFragmentKey,
} from './utils/crypto';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '/api';

const apiClient = axios.create({
  timeout: API_TIMEOUT_MS,
});

function App() {
  const [currentPage, setCurrentPage] = useState('home');
  const [pricingTab, setPricingTab] = useState('hosted');
  const [copiedCliBlock, setCopiedCliBlock] = useState(null);
  const [themeMode, setThemeMode] = useState(() => localStorage.getItem('themeMode') || 'auto');

  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [secretData, setSecretData] = useState('');
  const [wrapTtl, setWrapTtl] = useState('1h');
  const [wrappedToken, setWrappedToken] = useState('');
  const [wrappedTokens, setWrappedTokens] = useState([]);
  const [numLinks, setNumLinks] = useState(1);
  const [enableIPRestriction, setEnableIPRestriction] = useState(false);
  const [customAllowedIPs, setCustomAllowedIPs] = useState('');
  const [showQRCodes, setShowQRCodes] = useState({});
  const [shareKey, setShareKey] = useState('');
  const [showEncryptResult, setShowEncryptResult] = useState(false);
  const [isSharedLink, setIsSharedLink] = useState(false);
  const [isDecrypting, setIsDecrypting] = useState(false);
  const [isEncrypting, setIsEncrypting] = useState(false);
  const [sharedLinkKey, setSharedLinkKey] = useState('');
  const [linkCopied, setLinkCopied] = useState(false);
  const [secretCopied, setSecretCopied] = useState(false);

  const [enableNotification, setEnableNotification] = useState(false);
  const [notificationEmail, setNotificationEmail] = useState('');
  const [emailValidation, setEmailValidation] = useState({ isValid: null, message: '' });

  const [linkCreatedAt, setLinkCreatedAt] = useState('');
  const [linkCreatedAtMs, setLinkCreatedAtMs] = useState(null);

  const [turnstileToken] = useState(null);

  const [unwrappedData, setUnwrappedData] = useState('');
  const [pendingUnwrap, setPendingUnwrap] = useState(null);

  const encryptingRef = useRef(false);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const tokenFromUrl = urlParams.get('token');
    if (tokenFromUrl) {
      setIsSharedLink(true);
      const keyFromFragment = readKeyFromLocation();
      if (keyFromFragment) {
        setSharedLinkKey(keyFromFragment);
      }
      setPendingUnwrap({ token: tokenFromUrl, keyFragment: keyFromFragment });
      window.history.replaceState({}, document.title, window.location.pathname);
      if (!keyFromFragment || !isValidFragmentKey(keyFromFragment)) {
        setError('This link looks incomplete. Copy the entire URL you received, including the part after #.');
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('authToken');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const handleEncrypt = async () => {
    await wrapSecret();
  };

  const wrapSecret = async () => {
    if (encryptingRef.current || isEncrypting) {
      return;
    }

    if (!secretData.trim()) {
      setError('Enter a secret to share.');
      return;
    }

    if (secretData.length > MAX_SECRET_LENGTH) {
      setError(`Secret is too long. Shorten it to ${MAX_SECRET_LENGTH.toLocaleString()} characters or less.`);
      return;
    }

    encryptingRef.current = true;
    setIsEncrypting(true);
    setError('');
    setSuccess('');

    try {
      const aesKey = await generateAesKey();
      const keyFragment = await exportKeyToFragment(aesKey);
      const { ciphertext, nonce } = await encryptSecret(secretData, aesKey);

      if (enableNotification) {
        if (!notificationEmail.trim()) {
          setError('Enter an email address to receive open notifications.');
          setIsEncrypting(false);
          encryptingRef.current = false;
          return;
        }
        const validation = validateEmail(notificationEmail);
        if (!validation.isValid) {
          setError('Enter a valid email address, like you@company.com.');
          setIsEncrypting(false);
          encryptingRef.current = false;
          return;
        }
      }

      const requestData = {
        ciphertext,
        nonce,
        wrapTtl,
        namespace: 'public',
        numLinks,
        turnstileToken,
      };

      if (enableNotification && notificationEmail.trim()) {
        requestData.notificationEmail = notificationEmail.trim();
      }

      if (enableIPRestriction) {
        if (customAllowedIPs.trim()) {
          requestData.allowedIPs = customAllowedIPs.split(',').map((ip) => ip.trim()).filter(Boolean);
        } else {
          requestData.restrictToCurrentIP = true;
        }
      }

      const response = await apiClient.post(`${API_BASE_URL}/wrap`, requestData);

      if (response.data.wrap_infos) {
        setWrappedTokens(response.data.wrap_infos);
        setWrappedToken('');
        const qrInitialState = {};
        response.data.wrap_infos.forEach((_, index) => {
          qrInitialState[index] = false;
        });
        setShowQRCodes(qrInitialState);
      } else {
        setWrappedToken(response.data.wrap_info.token);
        setWrappedTokens([]);
        setShowQRCodes({ single: false });
      }

      setShareKey(keyFragment);
      setLinkCreatedAtMs(Date.now());
      setLinkCreatedAt(
        `just now, ${new Date().toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`,
      );
      setShowEncryptResult(true);
      setIsEncrypting(false);
      encryptingRef.current = false;
    } catch (err) {
      setError(getApiErrorMessage(err, 'Could not create the link. Check your connection and try again.'));
      console.error('Wrap error:', err);
      setIsEncrypting(false);
      encryptingRef.current = false;
    }
  };

  const unwrapSecretFromUrl = async (token, keyFragment) => {
    setError('');
    setSuccess('');
    setIsDecrypting(true);

    const keyStr = keyFragment || sharedLinkKey;
    if (!keyStr || !isValidFragmentKey(keyStr)) {
      setError('This link looks incomplete. Copy the entire URL you received, including the part after #.');
      setIsDecrypting(false);
      setPendingUnwrap(null);
      return;
    }

    try {
      const response = await apiClient.post(`${API_BASE_URL}/unwrap`, { token }, { headers: getAuthHeaders() });
      const unwrapped = response.data.data;
      const ciphertext = unwrapped && unwrapped.ciphertext;
      const nonce = unwrapped && unwrapped.nonce;

      if (!ciphertext || !nonce) {
        setError('We could not read this secret. The link may be broken or from an older version.');
        setIsDecrypting(false);
        return;
      }

      const aesKey = await importKeyFromFragment(keyStr);
      let plaintext;
      try {
        plaintext = await decryptSecret(ciphertext, nonce, aesKey);
      } catch (decryptErr) {
        setError('Could not decrypt this secret. Make sure you copied the entire link, including the part after #.');
        setIsDecrypting(false);
        console.error('Decrypt error:', decryptErr);
        return;
      }

      setUnwrappedData(plaintext);
      setSuccess('');
      setIsDecrypting(false);
      setPendingUnwrap(null);
    } catch (err) {
      const errorMessage = getApiErrorMessage(
        err,
        'Could not open this link. It may have expired or already been used.'
      );
      setError(errorMessage);
      if (isConsumedOrExpiredError(errorMessage) || isConsumedOrExpiredError(err.response?.data?.error)) {
        setUnwrappedData('');
        setSuccess('');
        setPendingUnwrap(null);
      }
      setIsDecrypting(false);
      console.error('Unwrap error:', err);
    }
  };

  const retryUnwrap = () => {
    if (!pendingUnwrap?.token) return;
    unwrapSecretFromUrl(pendingUnwrap.token, pendingUnwrap.keyFragment || sharedLinkKey);
  };

  const validateEmail = (email) => {
    if (!email.trim()) {
      return { isValid: null, message: '' };
    }

    const trimmedEmail = email.trim();
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    const hasValidStructure = emailRegex.test(trimmedEmail);
    const hasOnlyOneAt = (trimmedEmail.match(/@/g) || []).length === 1;
    const doesntStartOrEndWithDot = !trimmedEmail.startsWith('.') && !trimmedEmail.endsWith('.');
    const doesntHaveConsecutiveDots = !trimmedEmail.includes('..');
    const localPart = trimmedEmail.split('@')[0];
    const domainPart = trimmedEmail.split('@')[1];

    const localPartValid = localPart
      && localPart.length <= 64
      && !localPart.startsWith('.')
      && !localPart.endsWith('.');

    const domainPartValid = domainPart
      && domainPart.length <= 253
      && !domainPart.startsWith('.')
      && !domainPart.endsWith('.')
      && !domainPart.startsWith('-')
      && !domainPart.endsWith('-');

    const isValid = hasValidStructure
      && hasOnlyOneAt
      && doesntStartOrEndWithDot
      && doesntHaveConsecutiveDots
      && localPartValid
      && domainPartValid;

    if (!isValid) {
      return { isValid: false, message: 'Please enter a valid email address' };
    }

    return { isValid: true, message: 'Valid email address' };
  };

  const generateSecretLink = (token) => {
    const baseUrl = window.location.origin;
    const base = `${baseUrl}?token=${encodeURIComponent(token)}`;
    return shareKey ? `${base}#k=${shareKey}` : base;
  };

  const resetEncryptForm = () => {
    setSecretData('');
    setWrappedToken('');
    setWrappedTokens([]);
    setNumLinks(1);
    setShareKey('');
    setShowEncryptResult(false);
    setError('');
    setSuccess('');
    setEnableNotification(false);
    setNotificationEmail('');
    setEmailValidation({ isValid: null, message: '' });
    setEnableIPRestriction(false);
    setCustomAllowedIPs('');
    setShowQRCodes({});
    setLinkCreatedAt('');
    setLinkCreatedAtMs(null);
    setLinkCopied(false);
    setSecretCopied(false);
    setIsEncrypting(false);
  };

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(''), 5000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [success]);

  useEffect(() => {
    if (error && !isSharedLink) {
      const timer = setTimeout(() => setError(''), 8000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [error, isSharedLink]);

  // Theme: persist the preference and mirror the resolved value onto <html>.
  useEffect(() => {
    const root = document.documentElement;
    const media = typeof window !== 'undefined' && window.matchMedia
      ? window.matchMedia('(prefers-color-scheme: dark)')
      : null;

    const apply = () => {
      const dark = themeMode === 'dark' || (themeMode === 'auto' && Boolean(media && media.matches));
      root.classList.toggle('dark', dark);
    };

    apply();
    localStorage.setItem('themeMode', themeMode);

    if (themeMode === 'auto' && media) {
      media.addEventListener('change', apply);
      return () => media.removeEventListener('change', apply);
    }
    return undefined;
  }, [themeMode]);

  const handleHeaderNavigate = (page, options = {}) => {
    if (page === 'home') {
      setCurrentPage('home');
      setIsSharedLink(false);
      setUnwrappedData('');
      setError('');
      setSuccess('');
      setIsDecrypting(false);
      setPendingUnwrap(null);
      setSharedLinkKey('');
      resetEncryptForm();
    } else {
      setCurrentPage(page);
      if (page === 'pricing' && options.tab) {
        setPricingTab(options.tab);
      } else if (page !== 'pricing') {
        setPricingTab('hosted');
      }
    }
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, behavior: 'auto' });
    }
  };

  const copyCliCommands = async (text, block) => {
    try {
      await copyToClipboard(text);
      setCopiedCliBlock(block);
      setTimeout(() => setCopiedCliBlock(null), 2000);
    } catch {
      setError('Could not copy to clipboard.');
    }
  };

  const showGlobalAlerts = (error && !isSharedLink) || success;

  return (
    <div className="min-h-screen w-full overflow-x-hidden flex flex-col bg-canvas text-fg">
      <AppHeader
        currentPage={currentPage}
        onNavigate={handleHeaderNavigate}
        themeMode={themeMode}
        setTheme={setThemeMode}
      />

      <main className="flex-1 w-full max-w-page mx-auto safe-x min-w-0">
        {showGlobalAlerts && (
          <div className="w-full max-w-tool mx-auto pt-6 -mb-2 sm:-mb-4 animate-rise-fast">
            {error && !isSharedLink && (
              <div className={ALERT_ERROR} role="alert" aria-live="assertive">
                <AlertCircle className="h-[18px] w-[18px] shrink-0 mt-px" aria-hidden="true" />
                <span className="break-words min-w-0">{error}</span>
              </div>
            )}
            {success && (
              <div className={ALERT_SUCCESS} role="status" aria-live="polite">
                <Check className="h-[18px] w-[18px] shrink-0 mt-px" aria-hidden="true" />
                <span className="break-words min-w-0">{success}</span>
              </div>
            )}
          </div>
        )}

        {currentPage === 'home' && (
          <HomePage
            isSharedLink={isSharedLink}
            unwrappedData={unwrappedData}
            error={error}
            isDecrypting={isDecrypting}
            secretCopied={secretCopied}
            setSecretCopied={setSecretCopied}
            setError={setError}
            showEncryptResult={showEncryptResult}
            secretData={secretData}
            setSecretData={setSecretData}
            handleEncrypt={handleEncrypt}
            isEncrypting={isEncrypting}
            wrapTtl={wrapTtl}
            setWrapTtl={setWrapTtl}
            enableNotification={enableNotification}
            setEnableNotification={setEnableNotification}
            notificationEmail={notificationEmail}
            setNotificationEmail={setNotificationEmail}
            emailValidation={emailValidation}
            validateEmail={validateEmail}
            setEmailValidation={setEmailValidation}
            numLinks={numLinks}
            setNumLinks={setNumLinks}
            enableIPRestriction={enableIPRestriction}
            setEnableIPRestriction={setEnableIPRestriction}
            customAllowedIPs={customAllowedIPs}
            setCustomAllowedIPs={setCustomAllowedIPs}
            wrappedToken={wrappedToken}
            wrappedTokens={wrappedTokens}
            showQRCodes={showQRCodes}
            setShowQRCodes={setShowQRCodes}
            linkCopied={linkCopied}
            setLinkCopied={setLinkCopied}
            linkCreatedAt={linkCreatedAt}
            linkCreatedAtMs={linkCreatedAtMs}
            generateSecretLink={generateSecretLink}
            resetEncryptForm={resetEncryptForm}
            onCreateNew={() => handleHeaderNavigate('home')}
            onReveal={() => {
              if (pendingUnwrap?.token) {
                unwrapSecretFromUrl(pendingUnwrap.token, pendingUnwrap.keyFragment || sharedLinkKey);
              }
            }}
            canReveal={Boolean(pendingUnwrap?.token) && !isDecrypting}
            hasValidKey={isValidFragmentKey(pendingUnwrap?.keyFragment || sharedLinkKey)}
            onRetryUnwrap={retryUnwrap}
            canRetryUnwrap={Boolean(pendingUnwrap?.token) && !isDecrypting}
            onNavigate={handleHeaderNavigate}
          />
        )}

        {currentPage === 'about' && (
          <AboutPage onNavigateHome={() => handleHeaderNavigate('home')} />
        )}

        {currentPage === 'cli' && (
          <CliPage
            copiedCliBlock={copiedCliBlock}
            onCopyMac={() => copyCliCommands('brew trust nvteh/brew-tools\nbrew tap nvteh/brew-tools\nbrew install hushify', 'mac')}
            onCopyLinux={() => copyCliCommands('curl -fsSL https://www.hushify.io/install | sh', 'linux')}
          />
        )}

        {currentPage === 'use-cases' && (
          <UseCasesPage onNavigateHome={() => handleHeaderNavigate('home')} />
        )}

        {currentPage === 'pricing' && <PricingPage initialTab={pricingTab} />}

        {currentPage === 'privacy' && <PrivacyPage />}
      </main>

      <footer className="mt-auto rule-fade-t safe-bottom">
        <div className="max-w-page mx-auto safe-x pt-10 pb-8 sm:pt-12">
          <div className="flex flex-col gap-10 md:flex-row md:items-start md:justify-between">
            <div className="max-w-xs">
              <div className="flex items-center gap-2.5">
                <span className="inline-flex h-6 w-6 items-center justify-center rounded-[6px] bg-accent text-accent-fg">
                  <Shield className="h-3.5 w-3.5" strokeWidth={2.25} aria-hidden="true" />
                </span>
                <span className="text-[15px] font-semibold tracking-[-0.01em] text-fg">hushify</span>
              </div>
              <p className={`${TEXT.small} mt-3 leading-relaxed`}>
                One-time links for secrets. Encrypted in your browser before anything is uploaded, gone the moment it is read.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-10 sm:gap-20">
              <div>
                <p className={`${TEXT.eyebrow} mb-3.5`}>Product</p>
                <ul className="flex flex-col gap-2.5">
                  {PRIMARY_PAGES.map(({ id, label }) => (
                    <li key={id}>
                      <button type="button" onClick={() => handleHeaderNavigate(id)} className={FOOTER_LINK}>
                        {id === 'home' ? 'Share a secret' : label}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <p className={`${TEXT.eyebrow} mb-3.5`}>Company</p>
                <ul className="flex flex-col gap-2.5">
                  {SECONDARY_PAGES.map(({ id, label }) => (
                    <li key={id}>
                      <button type="button" onClick={() => handleHeaderNavigate(id)} className={FOOTER_LINK}>
                        {label}
                      </button>
                    </li>
                  ))}
                  <li>
                    <a href="https://www.nvteh.com" target="_blank" rel="noopener noreferrer" className={FOOTER_LINK}>
                      NVTEH
                    </a>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <div className="mt-10 pt-5 border-t border-line flex flex-col gap-1.5 sm:flex-row sm:items-center sm:justify-between text-[13px] text-subtle">
            <p>© {new Date().getFullYear()} NVTEH. All rights reserved.</p>
            <p>hushify.io · Encrypted in your browser</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;
